import axios from "axios";
import { URLEndpoints } from "../../constants/urlConstants";
import { normalizeReportKey } from "../../pages/instances/reports/shared/reportKeys";

const BASE_URL = process.env.REACT_APP_NODEJS_SERVER_URL;
const TTL = 5 * 60 * 1000;
const REPORT_DEFINITIONS_EVENT = "elitecore:report-definitions-invalidated";

const getToken = () => localStorage.getItem("accessToken");
const authHeaders = () => {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const cacheScope = () => {
  const token = getToken();
  return token ? token.slice(-24) : "anonymous";
};
const defKey = (instanceUniqueId, reportKey, id) => `rd:${cacheScope()}:def:${instanceUniqueId}:${reportKey || "all"}:${id}`;
const listPrefix = (instanceUniqueId) => `rd:${cacheScope()}:list:${instanceUniqueId}:`;
const listKey = (instanceUniqueId, reportKey = "all") => `${listPrefix(instanceUniqueId)}${reportKey || "all"}`;

const readCache = (key) => {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.ts || typeof parsed.data === "undefined") return null;
    if (Date.now() - parsed.ts > TTL) return null;
    return parsed.data;
  } catch {
    return null;
  }
};

const writeCache = (key, data) => {
  try {
    sessionStorage.setItem(key, JSON.stringify({ ts: Date.now(), data }));
  } catch {
    // Ignore storage failures.
  }
};

const invalidate = (key) => {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // Ignore storage failures.
  }
};

const invalidateDefinitionLists = (instanceUniqueId) => {
  if (!instanceUniqueId) return;
  try {
    const prefix = listPrefix(instanceUniqueId);
    const keysToRemove = [];
    for (let index = 0; index < sessionStorage.length; index += 1) {
      const key = sessionStorage.key(index);
      if (key && key.startsWith(prefix)) keysToRemove.push(key);
    }
    keysToRemove.forEach((key) => sessionStorage.removeItem(key));
  } catch {
    // Ignore storage failures.
  }
};

const topic = (instanceUniqueId, reportKey = "all") => `rd:invalidate:${instanceUniqueId}:${reportKey || "all"}`;

export const publishReportDefinitionsInvalidation = (instanceUniqueId, reportKey = "all") => {
  const normalizedReportKey = normalizeReportKey(reportKey, "all") || "all";
  try {
    const key = topic(instanceUniqueId, normalizedReportKey);
    localStorage.setItem(key, String(Date.now()));
    localStorage.removeItem(key);
  } catch {
    // Ignore storage failures.
  }
  try {
    window.dispatchEvent(new CustomEvent(REPORT_DEFINITIONS_EVENT, {
      detail: { instanceUniqueId, reportKey: normalizedReportKey },
    }));
  } catch {
    // Ignore event failures.
  }
};

export const subscribeReportDefinitionsInvalidation = (instanceUniqueId, callback, reportKey = "all") => {
  const normalizedReportKey = normalizeReportKey(reportKey, "all") || "all";
  const storageHandler = (event) => {
    if (event.key === topic(instanceUniqueId, normalizedReportKey) || event.key === topic(instanceUniqueId, "all")) {
      callback?.();
    }
  };
  const localHandler = (event) => {
    const detail = event?.detail || {};
    if (detail.instanceUniqueId !== instanceUniqueId) return;
    if (detail.reportKey && detail.reportKey !== "all" && detail.reportKey !== normalizedReportKey) return;
    callback?.();
  };
  window.addEventListener("storage", storageHandler);
  window.addEventListener(REPORT_DEFINITIONS_EVENT, localHandler);
  return () => {
    window.removeEventListener("storage", storageHandler);
    window.removeEventListener(REPORT_DEFINITIONS_EVENT, localHandler);
  };
};

export async function listReportDefinitions(instanceUniqueId, params = {}) {
  try {
    const normalizedReportKey = normalizeReportKey(params.reportKey, "all") || "all";
    const cacheId = listKey(instanceUniqueId, normalizedReportKey);
    const cached = readCache(cacheId);
    if (
      cached &&
      !params.q &&
      !params.tag &&
      !params.ownerUserId &&
      !params.limit &&
      !params.offset
    ) {
      return cached;
    }

    const res = await axios.get(
      `${BASE_URL}/${URLEndpoints.purchasingReport}/${URLEndpoints.reportDefinitions}`,
      {
        params: { instanceUniqueId, ...params, reportKey: normalizedReportKey === "all" ? undefined : normalizedReportKey },
        headers: authHeaders(),
      },
    );

    const data = res?.data?.data || [];
    if (!params.q && !params.tag && !params.ownerUserId && !params.limit && !params.offset) {
      writeCache(cacheId, data);
    }
    return data;
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
}

export async function createReportDefinition(payload, instanceUniqueId) {
  try {
    const normalizedReportKey = normalizeReportKey(payload?.reportKey || payload?.config_json?.reportKey, null);
    const body = {
      ...(payload || {}),
      reportKey: normalizedReportKey,
      instanceUniqueId: instanceUniqueId ?? payload?.instanceUniqueId,
    };
    const res = await axios.post(
      `${BASE_URL}/${URLEndpoints.purchasingReport}/${URLEndpoints.reportDefinitions}`,
      body,
      { headers: authHeaders() },
    );
    const inst = body.instanceUniqueId;
    if (inst) invalidateDefinitionLists(inst);
    if (inst) publishReportDefinitionsInvalidation(inst, normalizedReportKey || "all");
    return res?.data?.data;
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
}

export async function getReportDefinition(id, instanceUniqueId, params = {}) {
  try {
    const normalizedReportKey = normalizeReportKey(params.reportKey, "all") || "all";
    const cacheId = defKey(instanceUniqueId, normalizedReportKey, id);
    const cached = readCache(cacheId);
    if (cached) return cached;

    const res = await axios.get(
      `${BASE_URL}/${URLEndpoints.purchasingReport}/${URLEndpoints.reportDefinitions}/${id}`,
      {
        params: { instanceUniqueId, ...params, reportKey: normalizedReportKey === "all" ? undefined : normalizedReportKey },
        headers: authHeaders(),
      },
    );
    const data = res?.data?.data;
    if (data) writeCache(cacheId, data);
    return data;
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
}

export async function updateReportDefinition(id, payload, instanceUniqueId) {
  try {
    const normalizedReportKey = normalizeReportKey(payload?.reportKey || payload?.config_json?.reportKey, null);
    const body = {
      ...(payload || {}),
      reportKey: normalizedReportKey,
      ...(instanceUniqueId ? { instanceUniqueId } : {}),
    };
    const res = await axios.put(
      `${BASE_URL}/${URLEndpoints.purchasingReport}/${URLEndpoints.reportDefinitions}/${id}`,
      body,
      { headers: authHeaders() },
    );
    const data = res?.data?.data;
    invalidate(defKey(instanceUniqueId, normalizedReportKey || "all", id));
    const inst = instanceUniqueId || data?.instance_unique_id || payload?.instanceUniqueId;
    if (inst) invalidateDefinitionLists(inst);
    if (inst) publishReportDefinitionsInvalidation(inst, normalizedReportKey || "all");
    return data;
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
}

export async function deleteReportDefinition(id, instanceUniqueId, params = {}) {
  try {
    const normalizedReportKey = normalizeReportKey(params.reportKey, "all") || "all";
    const res = await axios.delete(
      `${BASE_URL}/${URLEndpoints.purchasingReport}/${URLEndpoints.reportDefinitions}/${id}`,
      {
        params: { instanceUniqueId, ...params, reportKey: normalizedReportKey === "all" ? undefined : normalizedReportKey },
        headers: authHeaders(),
      },
    );
    invalidate(defKey(instanceUniqueId, normalizedReportKey, id));
    if (instanceUniqueId) invalidateDefinitionLists(instanceUniqueId);
    if (instanceUniqueId) publishReportDefinitionsInvalidation(instanceUniqueId, normalizedReportKey);
    return res?.data || { ok: true };
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
}

export async function pinReportDefinition(id, { instanceUniqueId, reportKey, pinned }) {
  try {
    const normalizedReportKey = normalizeReportKey(reportKey, null);
    const res = await axios.patch(
      `${BASE_URL}/${URLEndpoints.purchasingReport}/${URLEndpoints.reportDefinitions}/${id}/pin`,
      {
        instanceUniqueId,
        reportKey: normalizedReportKey,
        pinned,
      },
      { headers: authHeaders() },
    );
    invalidate(defKey(instanceUniqueId, normalizedReportKey || "all", id));
    if (instanceUniqueId) invalidateDefinitionLists(instanceUniqueId);
    if (instanceUniqueId) publishReportDefinitionsInvalidation(instanceUniqueId, normalizedReportKey || "all");
    return res?.data?.data;
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
}
